import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import "./EmployeeLeaves.css";

const pad2 = (n) => String(n).padStart(2, "0");
const formatSlash = (y, m, d) => `${y}/${pad2(m)}/${pad2(d)}`;

const parseYmd = (str) => {
  if (!str) return null;
  const parts = String(str).slice(0, 10).split("-");
  if (parts.length !== 3) return null;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;
  return { y, m, d, date: new Date(y, m - 1, d) };
};

const getFiscalYearConfig = (fiscalStartMonth = 4) => {
  const today = new Date();
  const month = today.getMonth() + 1;
  const year = today.getFullYear();
  const currentFiscalYear = month >= fiscalStartMonth ? year : year - 1;
  return {
    fiscalStartMonth,
    currentFiscalYear,
    prevFiscalYear: currentFiscalYear - 1,
  };
};

const getFiscalPeriodBounds = (fiscalYear, fiscalStartMonth) => {
  const endMonth = fiscalStartMonth > 1 ? fiscalStartMonth - 1 : 12;
  const endYear = fiscalStartMonth > 1 ? fiscalYear + 1 : fiscalYear;
  const lastDay = new Date(endYear, endMonth, 0).getDate();
  return {
    start: new Date(fiscalYear, fiscalStartMonth - 1, 1),
    end: new Date(endYear, endMonth - 1, lastDay),
    labelStart: formatSlash(fiscalYear, fiscalStartMonth, 1),
    labelEnd: formatSlash(endYear, endMonth, lastDay),
  };
};

const extractDateListFromFormData = (formData) => {
  if (!formData || typeof formData !== "object") return [];
  let selected = formData.selectedDates;
  if (typeof selected === "string") {
    try {
      selected = JSON.parse(selected);
    } catch {
      selected = [];
    }
  }
  if (Array.isArray(selected) && selected.length > 0) {
    return selected.map((d) => String(d).slice(0, 10)).filter(Boolean);
  }
  const single = formData.selectedDate || formData.applicationDate || formData.start_date || formData.startDate || "";
  return single ? [String(single).slice(0, 10)] : [];
};

const buildDateStatusMap = (applications) => {
  const map = {};
  (applications || []).forEach((app) => {
    if (app.category !== "VACATION") return;
    if (app.status !== "SUBMITTED" && app.status !== "APPROVED") return;

    let formData = app.formData;
    if (typeof formData === "string") {
      try {
        formData = JSON.parse(formData);
      } catch {
        return;
      }
    }

    const dates = extractDateListFromFormData(formData);
    dates.forEach((dateStr) => {
      const parsed = parseYmd(dateStr);
      if (!parsed) return;
      const key = `${parsed.y}-${pad2(parsed.m)}-${pad2(parsed.d)}`;
      if (map[key] === "APPROVED") return;
      if (app.status === "APPROVED") {
        map[key] = "APPROVED";
      } else if (!map[key]) {
        map[key] = "SUBMITTED";
      }
    });
  });
  return map;
};

const datesInFiscalYear = (dateStatusMap, fiscalYear, fiscalStartMonth) => {
  const { start, end } = getFiscalPeriodBounds(fiscalYear, fiscalStartMonth);
  const rows = [];
  Object.entries(dateStatusMap).forEach(([dateStr, status]) => {
    const parsed = parseYmd(dateStr);
    if (!parsed) return;
    if (parsed.date < start || parsed.date > end) return;
    rows.push({
      dateStr,
      display: formatSlash(parsed.y, parsed.m, parsed.d),
      rawDate: parsed.date,
      status,
    });
  });
  rows.sort((a, b) => a.dateStr.localeCompare(b.dateStr));
  return rows;
};

const groupConsecutiveDates = (rows) => {
  if (rows.length === 0) return [];
  const resultBlocks = [];
  let currentGroup = [rows[0]];

  for (let i = 1; i < rows.length; i++) {
    const prev = currentGroup[currentGroup.length - 1];
    const curr = rows[i];
    const d1 = new Date(prev.rawDate.getFullYear(), prev.rawDate.getMonth(), prev.rawDate.getDate());
    const d2 = new Date(curr.rawDate.getFullYear(), curr.rawDate.getMonth(), curr.rawDate.getDate());
    const diffDays = Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));

    if (curr.status === prev.status && curr.category === prev.category && diffDays === 1) {
      currentGroup.push(curr);
    } else {
      resultBlocks.push(currentGroup);
      currentGroup = [curr];
    }
  }
  resultBlocks.push(currentGroup);

  const finalRows = [];
  resultBlocks.forEach((group) => {
    if (group.length > 1) {
      const first = group[0];
      const last = group[group.length - 1];
      finalRows.push({
        isRange: true,
        status: first.status,
        text: `${first.display} ～ ${last.display}`,
        category: first.category,
      });
    } else {
      const item = group[0];
      finalRows.push({
        isRange: false,
        status: item.status,
        text: item.display,
        category: item.category,
      });
    }
  });
  return finalRows;
};

const PeriodBlock = ({ rows }) => {
  const processedRows = useMemo(() => groupConsecutiveDates(rows), [rows]);
  const chunkedRender = useMemo(() => {
    const elements = [];
    let currentGridGroup = [];

    processedRows.forEach((row, idx) => {
      if (row.isRange) {
        if (currentGridGroup.length > 0) {
          elements.push({ type: "grid", data: currentGridGroup, key: `grid-${idx}` });
          currentGridGroup = [];
        }
        elements.push({ type: "range", data: row, key: `range-${idx}` });
      } else {
        currentGridGroup.push(row);
        if (currentGridGroup.length === 3) {
          elements.push({ type: "grid", data: currentGridGroup, key: `grid-${idx}` });
          currentGridGroup = [];
        }
      }
    });
    if (currentGridGroup.length > 0) {
      elements.push({ type: "grid", data: currentGridGroup, key: "grid-final" });
    }
    return elements;
  }, [processedRows]);

  if (processedRows.length === 0) {
    return <p className="elv-period-empty">該当する休暇日はありません。</p>;
  }

  return (
    <section className="elv-period-card">
      <div className="elv-date-container">
        {chunkedRender.map((block) => {
          if (block.type === "range") {
            const r = block.data;
            return (
              <div key={block.key} className={`elv-date-row-full elv-date-row--${r.status === "APPROVED" ? "approved" : "pending"}`}>
                <span className="elv-date-text">
                  {r.text} {r.category === "SERI" && <span className="elv-seiri-label">(生理休暇)</span>}
                </span>
              </div>
            );
          } else {
            return (
              <div key={block.key} className="elv-date-grid-row">
                {block.data.map((item, itemIdx) => (
                  <div key={itemIdx} className={`elv-date-grid-cell elv-date-row--${item.status === "APPROVED" ? "approved" : "pending"}`}>
                    <span className="elv-date-text" style={{ fontSize: "0.9rem" }}>
                      {item.text}
                      {item.category === "SERI" && <div className="elv-seiri-label" style={{ fontSize: "0.7rem" }}>(生理)</div>}
                    </span>
                  </div>
                ))}
                {block.data.length < 3 &&
                  Array.from({ length: 3 - block.data.length }).map((_, emptyIdx) => (
                    <div key={`empty-${emptyIdx}`} className="elv-date-grid-cell elv-date-grid-cell--empty"></div>
                  ))}
              </div>
            );
          }
        })}
      </div>
    </section>
  );
};

const VIEW_CONFIG = {
  approved: { label: "承認済み", statusFilter: "APPROVED" },
  pending: { label: "申請中", statusFilter: "SUBMITTED" },
  used: { label: "取得済み", statusFilter: "APPROVED" },
};

const EmployeeLeaves = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [remainingLeave, setRemainingLeave] = useState(null);
  const [fiscalStartMonth, setFiscalStartMonth] = useState(4);
  const [dateStatusMap, setDateStatusMap] = useState({});
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  const [dbLeaveInfo, setDbLeaveInfo] = useState(null);

  // 画面表示: "menu" | "approved" | "pending" | "used" | "yearly_report"
  const [view, setView] = useState("menu");

  const fiscalConfig = useMemo(() => getFiscalYearConfig(fiscalStartMonth), [fiscalStartMonth]);
  const fiscalYearOptions = useMemo(() => [fiscalConfig.currentFiscalYear, fiscalConfig.prevFiscalYear], [fiscalConfig]);
  const activeFiscalYear = selectedFiscalYear ?? fiscalConfig.currentFiscalYear;
  const todayStr = useMemo(() => {
    const today = new Date();
    return `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;
  }, []);

  const allRowsInFiscalYear = useMemo(() => {
    return datesInFiscalYear(dateStatusMap, activeFiscalYear, fiscalStartMonth);
  }, [dateStatusMap, activeFiscalYear, fiscalStartMonth]);

  const approvedRows = useMemo(() => allRowsInFiscalYear.filter((r) => r.status === "APPROVED" && r.dateStr >= todayStr), [allRowsInFiscalYear, todayStr]);
  const pendingRows = useMemo(() => allRowsInFiscalYear.filter((r) => r.status === "SUBMITTED"), [allRowsInFiscalYear]);
  const usedRows = useMemo(() => allRowsInFiscalYear.filter((r) => r.status === "APPROVED" && r.dateStr < todayStr), [allRowsInFiscalYear, todayStr]);

  const rowsByView = { approved: approvedRows, pending: pendingRows, used: usedRows };

  // 年度ごとに月の順番を会計年度開始月から並べるための配列
  const orderedMonths = useMemo(() => {
    const months = [];
    let m = fiscalStartMonth;
    for (let i = 0; i < 12; i++) {
      months.push(m);
      m++;
      if (m > 12) m = 1;
    }
    return months;
  }, [fiscalStartMonth]);

  // 月別の取得日数を計算するためのオブジェクト { "4": 1.0, "5": 0.0, ... }
  const monthlyDataReport = useMemo(() => {
    const report = {};
    orderedMonths.forEach((m) => { report[m] = 0; });

    // 当年度は バックエンドの monthlyUsed（＝直接修正データ ＋ 承認済み申請 の合算）を正とする。
    // 有給休暇マスター画面と同じ数字になり、申請せず直接DB修正した分も反映される。
    if (
      activeFiscalYear === fiscalConfig.currentFiscalYear &&
      dbLeaveInfo && dbLeaveInfo.monthlyUsed && typeof dbLeaveInfo.monthlyUsed === "object"
    ) {
      Object.entries(dbLeaveInfo.monthlyUsed).forEach(([k, v]) => {
        const m = parseInt(k, 10);
        if (report[m] !== undefined) report[m] = floatSafe(v);
      });
      return report;
    }

    // 過年度（またはバックエンド値が無い場合）は従来どおり承認済み申請から集計
    allRowsInFiscalYear.forEach((row) => {
      if (row.status === "APPROVED" && row.dateStr <= todayStr) {
        const parts = row.dateStr.split("-");
        const m = parseInt(parts[1], 10);
        if (report[m] !== undefined) {
          report[m] += 1.0;
        }
      }
    });
    return report;
  }, [allRowsInFiscalYear, orderedMonths, dbLeaveInfo, activeFiscalYear, fiscalConfig.currentFiscalYear, todayStr]);

  const totalUsedDays = useMemo(() => {
    return Object.values(monthlyDataReport).reduce((sum, val) => sum + val, 0);
  }, [monthlyDataReport]);

  const maxDaysInMonth = useMemo(() => {
    const max = Math.max(...Object.values(monthlyDataReport), 1.0);
    return max < 4 ? 4 : Math.ceil(max); 
  }, [monthlyDataReport]);

  const bounds = useMemo(() => getFiscalPeriodBounds(activeFiscalYear, fiscalStartMonth), [activeFiscalYear, fiscalStartMonth]);
  {/*}
  const calculatedEntitledDays = useMemo(() => {
    if (!dbLeaveInfo) return 0.0;
    if (activeFiscalYear === fiscalConfig.currentFiscalYear) {
      return floatSafe(dbLeaveInfo.currYearEntitled);
    } else {
      return floatSafe(dbLeaveInfo.prevYearEntitled);
    }
  }, [dbLeaveInfo, activeFiscalYear, fiscalConfig.currentFiscalYear]);
  */}
  const calculatedEntitledDays = useMemo(() => {
    if (!dbLeaveInfo) return 0.0;

    //年度ごとの付与日数を優先的に使用するロジック
    if (activeFiscalYear === fiscalConfig.currentFiscalYear) {
      if (dbLeaveInfo.currYearEntitled !== undefined && dbLeaveInfo.currYearEntitled !== null) {
        return floatSafe(dbLeaveInfo.currYearEntitled);
      }
    } 
    //前年度の付与日数がある場合はそちらを使用するロジック
    else {
      if (dbLeaveInfo.prevYearEntitled !== undefined && dbLeaveInfo.prevYearEntitled !== null) {
        return floatSafe(dbLeaveInfo.prevYearEntitled);
      }
    }

    return floatSafe(dbLeaveInfo.annualLeaveEntitled) || floatSafe(dbLeaveInfo.leaveEntitled) || 20.0;

  }, [dbLeaveInfo, activeFiscalYear, fiscalConfig.currentFiscalYear]);

  const displayRemainingLeave = useMemo(() => {
    if (!dbLeaveInfo) return 0.0;

    const prevEntitled = floatSafe(dbLeaveInfo.prevYearEntitled); // 前年の付与日数
    const prevUsed = floatSafe(dbLeaveInfo.prevYearUsed);         // 前年に消化した日数

    // 当年度: バックエンドの残数（remainingLeaveDays / leaveRemain）を正とし、
    // 有給残・有給詳細など他画面と一致させる（残数計算をバックエンドに一本化）。
    if (activeFiscalYear === fiscalConfig.currentFiscalYear) {
      const backendRemain = dbLeaveInfo.remainingLeaveDays ?? dbLeaveInfo.leaveRemain;
      if (backendRemain !== undefined && backendRemain !== null) {
        return Math.round(floatSafe(backendRemain) * 10) / 10;
      }
      // フォールバック（バックエンド値が無い場合のみ従来計算）
      const remain = prevEntitled + calculatedEntitledDays - totalUsedDays;
      return remain < 0 ? 0.0 : Math.round(remain * 10) / 10;
    }
    // 過年度: その年度の付与 − その年度の消化（繰越は足さない）
    // ※ バックエンドの残数は当年度基準のため、過年度はフロント計算を維持
    const remainLastYear = calculatedEntitledDays - prevUsed;
    return remainLastYear < 0 ? 0.0 : Math.round(remainLastYear * 10) / 10;
  }, [dbLeaveInfo, activeFiscalYear, fiscalConfig.currentFiscalYear, totalUsedDays, calculatedEntitledDays]);

  function floatSafe(val) {
    const res = parseFloat(val);
    return Number.isNaN(res) ? 0.0 : res;
  }

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [empRes, compRes, appsRes] = await Promise.all([
        api.get("/employee/me/"),
        api.get("/company-info/"),
        api.get("/employee/applications/"),
      ]);

      const emp = empRes.data?.employee || empRes.data;
      const memberId = emp?.memberId || emp?.employeeId;
      const fsm = Number(compRes.data?.fiscal_start_month);
      setFiscalStartMonth(fsm >= 1 && fsm <= 12 ? fsm : 4);

      if (memberId) {
        try {
          const leaveRes = await api.get(`/employees/${memberId}/leave/`, {
            params: { year: activeFiscalYear }
          });
          setRemainingLeave(leaveRes.data?.remainingLeaveDays ?? leaveRes.data?.leaveRemain ?? 0);
          setDbLeaveInfo(leaveRes.data);
        } catch {
          setRemainingLeave(0);
        }
      } else {
        setRemainingLeave(0);
      }

      const apps = Array.isArray(appsRes.data) ? appsRes.data : [];
      setDateStatusMap(buildDateStatusMap(apps));
    } catch (e) {
      console.error(e);
      setError("有給情報の取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, [activeFiscalYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="leave-page elv-page">
        <h1 className="elv-title">有給消化日</h1>
        <p className="elv-loading">読み込んでいます...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="leave-page elv-page">
        <h1 className="elv-title">有給消化日</h1>
        <p className="elv-error">{error}</p>
        <div className="btn-group">
          <button type="button" className="btn-back" onClick={fetchData}>再読み込み</button>
          <button type="button" className="btn-back" onClick={() => navigate("/card_back")}>戻る</button>
        </div>
      </div>
    );
  }

  // 1. 年度の月別取得数 (yearly_report)
  if (view === "yearly_report") {
    return (
      <div className="leave-page elv-page">
        <h1 className="elv-title">1年間の月別取得数</h1>
        <p className="elv-remain" style={{ fontSize: "0.85rem", marginBottom: "4px" }}>
          {bounds.labelStart} ~ {bounds.labelEnd} 会計年度
        </p>
        <p className="elv-remain" style={{ marginTop: "0px" }}>
          残り <strong>{displayRemainingLeave}</strong> 日
        </p>
        {activeFiscalYear === fiscalConfig.currentFiscalYear && dbLeaveInfo && parseFloat(dbLeaveInfo.prevYearEntitled) > 0 && (
          <span style={{ fontSize: '0.62rem', color: '#16a34a', fontWeight: '600', marginTop: '2px', display: 'block' }}>
          （前年度からの繰越 {parseFloat(dbLeaveInfo.prevYearEntitled).toFixed(1)}日含む）
          </span>
        )}
        
        <div className="elv-kpi-container">
          <div className="elv-kpi-box">
            <span className="elv-kpi-label">付与日数</span>
            <span className="elv-kpi-val elv-kpi-val--green">{calculatedEntitledDays.toFixed(1)}<span>日</span></span>
          </div>
          <div className="elv-kpi-box">
            <span className="elv-kpi-label">取得日数</span>
            <span className="elv-kpi-val elv-kpi-val--blue">{totalUsedDays.toFixed(1)}<span>日</span></span>
          </div>
          <div className="elv-kpi-box">
            <span className="elv-kpi-label">残り日数</span>
            <span className="elv-kpi-val elv-kpi-val--orange">{displayRemainingLeave.toFixed(1)}<span>日</span></span>
            
          </div>
          <div className="elv-kpi-box">
            <span className="elv-kpi-label">消化率</span>
            <span className="elv-kpi-val elv-kpi-val--dark">
              {calculatedEntitledDays > 0 ? ((totalUsedDays / calculatedEntitledDays) * 100).toFixed(1) : "0.0"}<span>%</span>
            </span>
          </div>
        </div>

      
        <h3 className="elv-section-subtitle">月別取得数（グラフ）</h3>
        <div className="elv-graph-wrapper">
          <div className="elv-y-axis">
            {Array.from({ length: maxDaysInMonth + 1 }).map((_, i) => (
              <span key={i} style={{ bottom: `${(i / maxDaysInMonth) * 100}%` }}>
                {i.toFixed(1)}
              </span>
            ))}
          </div>
          <div className="elv-graph-area">
            {orderedMonths.map((m) => {
              const days = monthlyDataReport[m] || 0;
              const barHeight = (days / maxDaysInMonth) * 100;
              return (
                <div key={m} className="elv-graph-col">
                  <div className="elv-graph-bar-container">
                    {days > 0 && <span className="elv-bar-val">{days.toFixed(1)}</span>}
                    <div className="elv-graph-bar" style={{ height: `${barHeight}%` }}></div>
                  </div>
                  <span className="elv-col-label">{m}月</span>
                </div>
              );
            })}
          </div>
        </div>

        
        <h3 className="elv-section-subtitle">月別取得数（一覧）</h3>
        <div className="elv-table-responsive">
          <table className="elv-report-table">
            <thead>
              <tr>
                <th className="elv-th-month">月</th>
                {orderedMonths.slice(0, 6).map((m) => <th className="elv-th-month" key={m}>{m}月</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>取得日数</td>
                {orderedMonths.slice(0, 6).map((m) => (
                  <td key={m} className={monthlyDataReport[m] > 0 ? "elv-td-has-val" : ""}>
                    {monthlyDataReport[m].toFixed(1)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          <table className="elv-report-table elv-report-table--second">
            <thead>
              <tr>
                <th className="elv-th-month">月</th>
                {orderedMonths.slice(6).map((m) => <th className="elv-th-month" key={m}>{m}月</th>)}
                <th className="elv-th-total">合計</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>取得日数</td>
                {orderedMonths.slice(6).map((m) => (
                  <td key={m} className={monthlyDataReport[m] > 0 ? "elv-td-has-val" : ""}>
                    {monthlyDataReport[m].toFixed(1)}
                  </td>
                ))}
                <td className="elv-td-total">{totalUsedDays.toFixed(1)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="btn-group elv-back-group">
          <button type="button" className="btn-back" onClick={() => setView("menu")}>戻る</button>
        </div>
      </div>
    );
  }

  // 2　メニュー画面
  if (view === "menu") {
    return (
      <div className="leave-page elv-page">
        <h1 className="elv-title">有給詳細</h1>
        {remainingLeave !== null && (
          <p className="elv-remain">残り <strong>{remainingLeave}</strong> 日</p>
        )}

        <div className="elv-fiscal-tabs" role="tablist" aria-label="表示する会計年度">
          {fiscalYearOptions.map((year) => (
            <button
              key={year}
              type="button"
              role="tab"
              aria-selected={activeFiscalYear === year}
              className={`elv-fiscal-tab${activeFiscalYear === year ? " elv-fiscal-tab--active" : ""}`}
              onClick={() => setSelectedFiscalYear(year)}
            >
              {year}年度
            </button>
          ))}
        </div>

        <div className="elv-menu-buttons">
          {/* メニュー項目 */}
          <button
            type="button"
            className="elv-menu-button elv-menu-button--report"
            onClick={() => setView("yearly_report")}
          >
            1年間の月別取得数を表示
          </button>
          
          <button type="button" className="elv-menu-button elv-menu-button--approved" onClick={() => setView("approved")}>
            承認済（{approvedRows.length}件）
          </button>
          <button type="button" className="elv-menu-button elv-menu-button--pending" onClick={() => setView("pending")}>
            申請中（{pendingRows.length}件）
          </button>
          <button type="button" className="elv-menu-button elv-menu-button--used" onClick={() => setView("used")}>
            取得済（{usedRows.length}件）
          </button>
        </div>

        <div className="btn-group elv-back-group">
          <button type="button" className="btn-back" onClick={() => navigate("/card_back")}>戻る</button>
        </div>
      </div>
    );
  }

  // 画面詳細（Approved / Pending / Used）
  const cfg = VIEW_CONFIG[view];
  const detailRows = rowsByView[view] || [];

  return (
    <div className="leave-page elv-page">
      <h1 className="elv-title">{`${activeFiscalYear}年度 ${cfg.label}`}</h1>
      <PeriodBlock rows={detailRows} view={view} />
      <div className="btn-group elv-back-group">
        <button type="button" className="btn-back" onClick={() => setView("menu")}>戻る</button>
      </div>
    </div>
  );
};

export default EmployeeLeaves;